<template>
  <div class="content-card-browser">

    <!-- Controls bar -->
    <div class="controls-bar">
      <LocaleSelector
        v-model="activeLang"
        :locales="SUPPORTED_LOCALES"
        :labels="LOCALE_LABELS"
        aria-label="Display language"
      />

      <div class="controls-right">
        <input
          v-model="searchInput"
          type="search"
          class="search-input"
          placeholder="Search by title"
          autocomplete="off"
          spellcheck="false"
        >

        <div class="type-pills" role="group" aria-label="Type filter">
          <button
            type="button"
            :class="['pill', { 'is-active': typeFilter === 'all' }]"
            @click="typeFilter = 'all'"
          >All</button>
          <button
            v-for="t in TYPE_VALUES"
            :key="t"
            type="button"
            :class="[
              'pill',
              `pill-${t}`,
              { 'is-active': typeFilter === t },
            ]"
            @click="typeFilter = t"
          >{{ typeDisplayLabel(t) }}</button>
        </div>
      </div>
    </div>

    <!-- States + body -->
    <p v-if="loadError" class="error-banner">{{ loadError }}</p>

    <p v-else-if="loading" class="loading-state">Loading content cards…</p>

    <div
      v-else-if="cards.length === 0"
      class="empty-state empty-full"
    >
      <h2 class="empty-title">No content cards yet</h2>
      <p class="empty-text">
        Create some at
        <router-link to="/admin/cards/edit" class="empty-link">/admin/cards/edit</router-link>.
      </p>
    </div>

    <div
      v-else-if="visibleCards.length === 0"
      class="empty-state empty-soft"
    >
      <p>No matches. Adjust filter or search.</p>
      <button
        type="button"
        class="btn-link"
        @click="clearFilters"
      >Clear filters</button>
    </div>

    <div v-else class="card-table-wrap">
      <table class="card-table">
        <thead>
          <tr>
            <th class="th-type">Type</th>
            <th class="th-title">Title</th>
            <th class="th-updated">Updated</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="card in visibleCards"
            :key="card.id"
            :class="{ 'row-excluded': isExcluded(card.id) }"
            :tabindex="0"
            @click="onRowClick(card)"
            @keydown.enter.prevent="onRowClick(card)"
            @keydown.space.prevent="onRowClick(card)"
          >
            <td>
              <span class="type-chip" :class="`type-${card.type}`">
                {{ typeDisplayLabel(card.type) }}
              </span>
            </td>
            <td class="td-title">
              <div class="td-title-stack">
                <span class="title-text">{{ titleFor(card, activeLang) || '—' }}</span>
                <span
                  v-if="isExcluded(card.id)"
                  class="excluded-badge"
                  title="Already in this section"
                >Already in this section</span>
              </div>
            </td>
            <td
              class="td-updated"
              :title="formatAbsolute(card.updated_at)"
            >{{ formatRelative(card.updated_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
// ContentCardBrowser
//
// Picker-only component (no standalone mode). Lists content cards inside
// the AddCardModal's "Content cards" tab. The row click emits
// `select-card` with the full card row and the active locale, so the
// modal's toast can resolve the title with a sensible locale fallback.
//
// Data: fixture only for Step 1. The backend GET /api/seq/content-cards
// endpoint does not exist yet. Replace the fixture import with the real
// apiService.fetchContentCards() call when it's ready — see TODO below.
//
// Title resolution: the title lives in the block whose kind === 'TITLE'.
// blocks[0] is NOT always the title — block ordering is not a contract.
// findByKind('TITLE') is the correct lookup.

import LocaleSelector from '@/components/admin/LocaleSelector.vue';
import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
} from '@/components/admin/_data/questions.js';
import {
  contentCardsLibraryState,
  loadContentCardsLibrary,
} from '@/components/admin/_data/contentCardsLibrary.js';

// Lowercase DB values; uppercase / spaced for display. SECTION_COMPLETE
// was added during the junction Step 2 cutover for between-section
// congratulations screens.
const TYPE_VALUES = ['factoid', 'recipe', 'tip', 'section_complete'];
const TYPE_DISPLAY_LABEL = {
  factoid: 'FACTOID',
  recipe: 'RECIPE',
  tip: 'TIP',
  section_complete: 'SECTION COMPLETE',
};

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Find the title text on a card for a specific locale. Walks blocks to
 * find the TITLE block (NOT blocks[0]) and returns its locale value
 * after trimming. Returns empty string when missing.
 */
export function titleForLocale(card, locale) {
  const titleBlock = card?.blocks?.find(b => b.kind === 'TITLE');
  if (!titleBlock || !titleBlock.text) return '';
  const v = titleBlock.text[locale];
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Resolve a card title with a fallback chain: prefer the requested
 * locale, then English, then any non-empty locale, then empty.
 * Used by the toast in AddCardModal so a French-mode user still sees
 * something meaningful when the card was only authored in EN/DE.
 */
export function titleWithFallback(card, preferLocale) {
  const direct = titleForLocale(card, preferLocale);
  if (direct) return direct;
  const order = ['en', 'de', 'fr', 'it'].filter(l => l !== preferLocale);
  for (const loc of order) {
    const v = titleForLocale(card, loc);
    if (v) return v;
  }
  return '';
}

export default {
  name: 'ContentCardBrowser',
  components: { LocaleSelector },
  props: {
    // Step 1: accepted but only used to render an "Already in this
    // section" badge and disable selection on matching rows. The
    // AddCardModal passes [] today; Step 2 will pass the actual list
    // of card ids currently attached to the section.
    excludeCardIds: { type: Array, default: () => [] },
  },
  emits: ['select-card'],
  data() {
    return {
      SUPPORTED_LOCALES,
      LOCALE_LABELS,
      TYPE_VALUES,
      activeLang: DEFAULT_LOCALE,
      searchInput: '',
      searchQuery: '',
      searchDebounceTimer: null,
      typeFilter: 'all',
    };
  },
  computed: {
    // Reactive pass-throughs to the shared library store so this
    // component re-renders when the fetch lands or another consumer
    // refreshes the store.
    cards() {
      return contentCardsLibraryState.items;
    },
    loading() {
      return contentCardsLibraryState.loading
        && contentCardsLibraryState.items.length === 0;
    },
    loadError() {
      return contentCardsLibraryState.loadError;
    },
    excludedSet() {
      return new Set(this.excludeCardIds || []);
    },
    /**
     * Filtered + sorted cards. Search runs across all four locales' title
     * text so a DE-tab user can still find a card they remember by its
     * English title. Default sort: updated_at descending.
     */
    visibleCards() {
      // Show only active rows. Legacy uppercase status values
      // ('APPROVED' from before the lowercase cleanup) are also treated
      // as live during the migration window — drop the 'APPROVED' check
      // once the DB is confirmed lowercase across the board.
      let rows = this.cards.filter(
        c => c.status === 'active' || c.status === 'APPROVED',
      );

      if (this.typeFilter !== 'all') {
        rows = rows.filter(c => c.type === this.typeFilter);
      }

      const q = (this.searchQuery || '').trim().toLowerCase();
      if (q) {
        rows = rows.filter(card => {
          for (const loc of SUPPORTED_LOCALES) {
            const v = titleForLocale(card, loc);
            if (v && v.toLowerCase().includes(q)) return true;
          }
          return false;
        });
      }

      // updated_at desc (most recently edited first). No clickable
      // column sort for Step 1 — matches the existing Card Editor's
      // flat list ordering.
      return [...rows].sort((a, b) => {
        const av = a.updated_at || '';
        const bv = b.updated_at || '';
        if (av === bv) return 0;
        return av < bv ? 1 : -1;
      });
    },
  },
  watch: {
    searchInput(value) {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.searchQuery = value;
      }, 250);
    },
  },
  mounted() {
    this.fetchCards();
  },
  beforeUnmount() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
  },
  methods: {
    /**
     * Trigger a load through the shared store. Cheap on repeat: the
     * store de-dupes concurrent calls and short-circuits once loaded.
     * The component reads cards / loading / loadError reactively from
     * the store via the computeds above.
     */
    async fetchCards() {
      try {
        await loadContentCardsLibrary();
      } catch (e) {
        // The store has already set loadError; nothing else to do here.
      }
    },

    isExcluded(id) {
      return this.excludedSet.has(id);
    },

    titleFor(card, locale) {
      return titleForLocale(card, locale);
    },

    typeDisplayLabel(t) {
      // Lowercase DB value → spaced uppercase UI label. Falls back to
      // a plain uppercase of the value for any type we haven't mapped
      // (forward-compat with future card types).
      if (TYPE_DISPLAY_LABEL[t]) return TYPE_DISPLAY_LABEL[t];
      return (t || '').toUpperCase();
    },

    onRowClick(card) {
      // Junction Step 3: excluded rows are informational only. The
      // click still fires — duplicates are allowed. The "Already in
      // this section" badge tells the user this would create one.
      this.$emit('select-card', card, this.activeLang);
    },

    clearFilters() {
      this.typeFilter = 'all';
      this.searchInput = '';
      this.searchQuery = '';
    },

    formatRelative(iso) {
      if (!iso) return '';
      const then = new Date(iso).getTime();
      const now = Date.now();
      const diff = Math.max(0, now - then) / 1000;
      if (diff < 60) return 'just now';
      if (diff < 3600) {
        const m = Math.round(diff / 60);
        return `${m} minute${m === 1 ? '' : 's'} ago`;
      }
      if (diff < 86400) {
        const h = Math.round(diff / 3600);
        return `${h} hour${h === 1 ? '' : 's'} ago`;
      }
      if (diff < 86400 * 30) {
        const d = Math.round(diff / 86400);
        return `${d} day${d === 1 ? '' : 's'} ago`;
      }
      if (diff < 86400 * 365) {
        const m = Math.round(diff / 86400 / 30);
        return `${m} month${m === 1 ? '' : 's'} ago`;
      }
      const y = Math.round(diff / 86400 / 365);
      return `${y} year${y === 1 ? '' : 's'} ago`;
    },

    formatAbsolute(iso) {
      if (!iso) return '';
      return dateFmt.format(new Date(iso));
    },
  },
};
</script>

<style scoped>
.content-card-browser {
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-warn: #E8A33D;
  --color-warn-soft: rgba(232, 163, 61, 0.18);
  --color-warn-dark: #6B4A0F;
  --color-good: #2E7D32;
  --color-good-soft: rgba(46, 125, 50, 0.12);
  --color-bad: #C8392E;
  --color-bad-soft: rgba(200, 57, 46, 0.10);
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.content-card-browser *,
.content-card-browser *::before,
.content-card-browser *::after {
  box-sizing: border-box;
}

/* ───────── Controls bar ───────── */
.controls-bar {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.controls-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
}

.search-input {
  flex: 1 1 200px;
  padding: 8px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  font-family: inherit;
  font-size: 13px;
  color: var(--color-text-primary);
  min-width: 0;
}

.search-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.type-pills {
  display: inline-flex;
  gap: 4px;
  padding: 2px;
  background: var(--color-bg-soft);
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
}

.pill {
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 5px 10px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.pill:hover:not(.is-active) {
  background: var(--color-bg-muted);
}

.pill.is-active {
  background: var(--color-bg-card);
  color: var(--color-accent);
  box-shadow: 0 0 0 0.5px var(--color-border);
}

/* ───────── Banners + states ───────── */
.error-banner {
  font-size: 13px;
  color: #6B1F18;
  background: var(--color-bad-soft);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.25rem;
}

.loading-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-text-tertiary);
  font-size: 14px;
}

.empty-state {
  text-align: center;
  color: var(--color-text-secondary);
}

.empty-full {
  padding: 4rem 1rem;
  background: var(--color-bg-soft);
  border: 0.5px dashed var(--color-border);
  border-radius: var(--radius-lg);
}

.empty-soft {
  padding: 2.5rem 1rem;
  font-size: 14px;
}

.empty-soft p {
  margin: 0 0 8px;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 6px;
  color: var(--color-text-primary);
}

.empty-text {
  font-size: 14px;
  margin: 0 0 1.25rem;
}

.empty-link {
  color: var(--color-accent);
  text-decoration: none;
  border-bottom: 1px solid transparent;
}

.empty-link:hover {
  border-bottom-color: var(--color-accent);
}

.btn-link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: 13px;
  padding: 0;
  font-family: inherit;
  text-decoration: underline;
}

/* ───────── Table ───────── */
.card-table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
}

.card-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.card-table th,
.card-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 0.5px solid var(--color-border);
}

.card-table th {
  background: var(--color-bg-muted);
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-tertiary);
  position: sticky;
  top: 0;
  z-index: 1;
}

.th-type { width: 110px; }
.th-updated { width: 140px; white-space: nowrap; }
.th-title { /* fills remaining */ }

.card-table tbody tr {
  cursor: pointer;
  transition: background 0.1s;
}

.card-table tbody tr:hover {
  background: var(--color-bg-soft);
}

.card-table tbody tr:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: -2px;
}

/* Junction Step 3: excluded rows are informational only — click is
   still live, so the row shouldn't look disabled. Mild dim conveys
   "this is a duplicate" without screaming "don't click". */
.row-excluded {
  opacity: 0.85;
}

/* Type chips. Subtle tints so the picker doesn't shout — author scans
   the title column first, the type is a quick reinforcement. The
   existing Card Editor doesn't visually color-code types today, so
   there's no palette to mirror; these tints reuse the design system's
   accent/warn tokens plus a green for tips. */
.type-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.type-chip.type-factoid {
  background: var(--color-accent-soft);
  color: var(--color-accent-dark);
}

.type-chip.type-recipe {
  background: var(--color-warn-soft);
  color: var(--color-warn-dark);
}

.type-chip.type-tip {
  background: var(--color-good-soft);
  color: var(--color-good);
}

/* SECTION_COMPLETE — between-section congratulation cards. Neutral
   tint so it doesn't compete with the other three types' meanings. */
.type-chip.type-section_complete {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
  border: 0.5px solid var(--color-border);
}

/* Pills tinted the same way for fast association when toggling filters. */
.pill.pill-factoid.is-active          { color: var(--color-accent-dark); }
.pill.pill-recipe.is-active           { color: var(--color-warn-dark); }
.pill.pill-tip.is-active              { color: var(--color-good); }
.pill.pill-section_complete.is-active { color: var(--color-text-primary); }

/* Don't apply flex to the <td> itself — that knocks the cell out of
   `display: table-cell` and the row's border-bottom on this cell ends
   up at a different Y than its neighbours, producing the broken-line
   look. Put the flex on an inner wrapper instead. */
.td-title-stack {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.title-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.excluded-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  background: var(--color-bg-muted);
  padding: 1px 6px;
  border-radius: 4px;
  align-self: flex-start;
}

.td-updated {
  color: var(--color-text-secondary);
  white-space: nowrap;
}
</style>
